import httpClient from './httpClient';
import { parseApiError } from '../utils/errorHandler';

/**
 * Cliente reutilizável para upload de imagens via URLs pré-assinadas (ex.: DigitalOcean Spaces / S3).
 *
 * Fluxo:
 *  1. POST autenticado ao endpoint da API com ext → lista de { id, url, method, filename, expiresAt }
 *  2. Para cada arquivo: renomeia para `item.filename`, faz PUT direto para `item.url`
 *  3. Retorna array de IDs das imagens enviadas com sucesso
 *
 * O módulo não conhece regras de negócio — apenas solicita URLs e faz upload.
 * Pode ser usado por entregas, motoristas ou qualquer outro contexto que siga o mesmo padrão.
 */

/** @type {Set<string>} */
export const SUPPORTED_IMAGE_EXTENSIONS = new Set(['jpeg', 'jpg', 'png']);

const MIME_TO_EXTENSION = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
};

/**
 * @param {File} file
 * @returns {string} Extensão suportada pela API (jpeg, jpg ou png)
 */
export function getRequiredFileExtension(file) {
    const fromName =
        file?.name && file.name.includes('.')
            ? file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase()
            : '';

    if (fromName && SUPPORTED_IMAGE_EXTENSIONS.has(fromName)) {
        return fromName;
    }

    const fromMime = MIME_TO_EXTENSION[String(file?.type || '').toLowerCase()];
    if (fromMime) {
        return fromMime;
    }

    throw new Error(
        `Formato não suportado: "${file?.name || 'arquivo'}". Use PNG, JPG ou JPEG.`
    );
}

/**
 * @param {string} presignedEndpoint
 * @param {File[]} files
 * @returns {string}
 */
function appendRequiredExtensions(presignedEndpoint, files) {
    const extensions = files.map(getRequiredFileExtension);
    const [path, query = ''] = presignedEndpoint.split('?');
    const params = new URLSearchParams(query);
    extensions.forEach((ext) => params.append('ext', `.${ext}`));
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
}

/**
 * Solicita URLs pré-assinadas à API.
 *
 * @param {string} endpoint - Caminho completo com query params já montados.
 *   Ex.: `/api/entregas/2/imagens?customerId=2&ext=.png&ext=.jpg`
 * @returns {Promise<Array<{ id: number, url: string, method: string, filename: string, expiresAt: string }>>}
 */
async function requestPresignedUrls(endpoint) {
    const response = await httpClient.post(endpoint, null);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const json = await response.json();
    const items = json?.items;
    if (!Array.isArray(items)) {
        throw new Error('Resposta inesperada ao solicitar URLs de upload.');
    }
    return items;
}

/**
 * Faz upload de um único arquivo para uma URL pré-assinada.
 * Renomeia o arquivo para `item.filename` antes de enviar.
 *
 * O fetch é feito diretamente (sem httpClient) porque a URL já está assinada —
 * adicionar headers de autenticação da aplicação quebraria a assinatura do S3.
 *
 * @param {{ url: string, method: string, filename: string }} item - Item retornado pela API de presigned URLs
 * @param {File} file - Arquivo original selecionado pelo usuário
 * @returns {Promise<void>}
 */
async function uploadFile(item, file) {
    const renamed = new File([file], item.filename, { type: file.type });

    const response = await fetch(item.url, {
        method: item.method,
        headers: {
            'Content-Type': file.type,
            'x-amz-acl': 'public-read',
        },
        body: renamed,
    });

    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(
            `Falha ao enviar imagem "${file.name}" (HTTP ${response.status})${text ? `: ${text}` : ''}.`
        );
    }
}

/**
 * Orquestra o fluxo completo: solicita URLs pré-assinadas e faz upload sequencial dos arquivos.
 *
 * @param {string} presignedEndpoint - Caminho da API sem ext; elas serão acrescentadas aqui.
 *   Ex.: `/api/entregas/2/imagens?customerId=2`
 * @param {File[]} files - Lista de arquivos a enviar (máx. 15)
 * @param {{ onProgress?: (sent: number, total: number) => void }} [options]
 * @returns {Promise<number[]>} IDs das imagens enviadas (na mesma ordem dos arquivos)
 */
async function uploadAll(presignedEndpoint, files, { onProgress } = {}) {
    if (!files || files.length === 0) return [];

    const endpoint = appendRequiredExtensions(presignedEndpoint, files);
    const items = await requestPresignedUrls(endpoint);

    if (items.length < files.length) {
        throw new Error(
            `A API retornou ${items.length} URL(s) pré-assinada(s) mas ${files.length} arquivo(s) precisam ser enviados.`
        );
    }

    const uploadedIds = [];

    for (let i = 0; i < files.length; i++) {
        const item = items[i];
        await uploadFile(item, files[i]);
        uploadedIds.push(item.id);
        if (onProgress) {
            onProgress(i + 1, files.length);
        }
    }

    return uploadedIds;
}

const imageUploadClient = {
    requestPresignedUrls,
    uploadFile,
    uploadAll,
    getRequiredFileExtension,
    SUPPORTED_IMAGE_EXTENSIONS,
};

export default imageUploadClient;
